import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';

const AuthContext = createContext(null);

const PUBLIC_PATHS = ['/login', '/register', '/auth/callback', '/auth/callback/google', '/auth/callback/apple', '/auth/callback/meta', '/forbidden', '/telemarketing/login', '/telemarketing/pago-exitoso', '/telemarketing/pago-cancelado', '/pagar'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  const loadUserSession = useCallback(async () => {
    // Don't check auth on public paths
    if (PUBLIC_PATHS.some(path => location.pathname.startsWith(path))) {
      setLoading(false);
      return null;
    }

    try {
      const { data } = await api.get('/api/v1/auth/me');
      setUser(data);
      setPermissions(data.permissions);
      setIsAuthenticated(true);
      return data;
    } catch (error) {
      if (error.response?.status === 401) {
        // Try refresh once
        try {
          const { data: refreshData } = await api.post('/api/v1/auth/refresh');
          sessionStorage.setItem('access_token', refreshData.access_token);
          
          const { data: userData } = await api.get('/api/v1/auth/me');
          setUser(userData);
          setPermissions(userData.permissions);
          setIsAuthenticated(true);
          return userData;
        } catch (refreshError) {
          // Refresh failed - clear state
          setUser(null);
          setPermissions(null);
          setIsAuthenticated(false);
          sessionStorage.clear();
          throw refreshError;
        }
      }
      setUser(null);
      setPermissions(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    loadUserSession();
  }, [loadUserSession]);

  const login = async (email, password) => {
    const { data } = await api.post('/api/v1/auth/login', { email, password });
    
    sessionStorage.setItem('access_token', data.access_token);
    setUser(data.user);
    setPermissions(data.user.permissions);
    setIsAuthenticated(true);
    setLoading(false);
    
    return data.user;
  };

  const loginWithGoogle = async (sessionId) => {
    const { data } = await api.post('/api/v1/auth/google', { session_id: sessionId });
    
    sessionStorage.setItem('access_token', data.access_token);
    setUser(data.user);
    setPermissions(data.user.permissions);
    setIsAuthenticated(true);
    setLoading(false);
    
    return data.user;
  };

  const loginWithApple = async (authCode) => {
    // TODO: Implementar cuando se tengan credenciales de Apple
    const { data } = await api.post('/api/v1/auth/apple/callback', { code: authCode });
    
    sessionStorage.setItem('access_token', data.access_token);
    setUser(data.user);
    setPermissions(data.user.permissions);
    setIsAuthenticated(true);
    setLoading(false);
    
    return data.user;
  };

  const loginWithMeta = async (authCode) => {
    // TODO: Implementar cuando se tengan credenciales de Facebook App
    const { data } = await api.post('/api/v1/auth/meta/callback', { code: authCode });
    
    sessionStorage.setItem('access_token', data.access_token);
    setUser(data.user);
    setPermissions(data.user.permissions);
    setIsAuthenticated(true);
    setLoading(false);
    
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStorage.clear();
      setUser(null);
      setPermissions(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    permissions,
    isAuthenticated,
    isReadOnly: !!(permissions && permissions.is_read_only),
    loading,
    login,
    loginWithGoogle,
    loginWithApple,
    loginWithMeta,
    logout,
    refreshUser: loadUserSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
